import type { OutboxOp, OutboxOpKind } from './db'

/**
 * ตรรกะล้วนของคิว (ไม่มี IO) — แยกออกมาเพื่อให้เทสได้ตรง ๆ
 */

let counter = 0

/**
 * opId เรียงตามเวลาเสมอ: เวลา(base36) + ลำดับในหน่วยความจำ
 * ทำให้การส่งเรียงตามลำดับที่ผู้ใช้ทำจริง แม้จะสร้างหลายรายการในมิลลิวินาทีเดียวกัน
 */
export function nextOpId(now: number = Date.now()): string {
  counter = (counter + 1) % 1_000_000
  return `${now.toString(36).padStart(9, '0')}-${String(counter).padStart(6, '0')}`
}

export function createOp(
  kind: OutboxOpKind,
  submissionId: string,
  payload: Record<string, unknown>,
  now: number = Date.now(),
): OutboxOp {
  return {
    opId: nextOpId(now),
    kind,
    submissionId,
    payload,
    attempt: 0,
    nextAttemptAt: now,
    status: 'pending',
    createdAt: now,
  }
}

export const MAX_BACKOFF_MS = 5 * 60 * 1000

/** หน่วงเวลาก่อนลองใหม่: 2^n วินาที เพดาน 5 นาที + สุ่มเล็กน้อยกันชนกันเป็นระลอก */
export function backoffMs(attempt: number, jitter = Math.random()): number {
  const base = Math.min(2 ** attempt * 1000, MAX_BACKOFF_MS)
  return Math.round(base + jitter * Math.min(base * 0.25, 15_000))
}

export type OpOutcome =
  | { type: 'done' }
  /** ลองใหม่ภายหลัง (เน็ตล่ม/เซิร์ฟเวอร์ error ชั่วคราว) */
  | { type: 'retry'; message?: string }
  /** ล้มเหลวถาวร (ข้อมูลไม่ผ่านการตรวจ) — ต้องแจ้งผู้ใช้ ไม่ทิ้งข้อมูลเงียบ ๆ */
  | { type: 'failed'; message: string }
  /** ต้องล็อกอินใหม่ — หยุดคิวทั้งหมดไว้ก่อน ข้อมูลยังอยู่ครบ */
  | { type: 'unauthorized' }
  /** งานนี้ถูกลบไปแล้วที่ต้นทาง (เช่นแอดมินลบทิ้งจากหลังบ้าน) — เคลียร์ข้อมูลในเครื่องของงานนี้ทั้งหมด */
  | { type: 'gone' }

/** แปลงสถานะ HTTP เป็นการตัดสินใจของคิว */
export function classifyResponse(status: number, message: string): OpOutcome {
  if (status >= 200 && status < 300) return { type: 'done' }
  if (status === 401) return { type: 'unauthorized' }
  if (status === 0 || status === 408 || status === 429 || status >= 500) {
    return { type: 'retry', message }
  }
  // 404 = งาน/ทรัพยากรที่อ้างถึงหายไปจากต้นทางแล้ว (ถูกลบ) ต่างจาก 403 ที่แค่ไม่มีสิทธิ์
  if (status === 404) return { type: 'gone' }
  if (status === 403) return { type: 'failed', message }
  if (status === 400 || status === 413 || status === 422) return { type: 'failed', message }
  return { type: 'retry', message }
}

/** รายการที่ถึงเวลาส่งแล้ว เรียงตามลำดับที่ผู้ใช้ทำ */
export function readyOps(ops: readonly OutboxOp[], now: number = Date.now()): OutboxOp[] {
  return ops
    .filter((op) => op.status === 'pending' && op.nextAttemptAt <= now)
    .sort((a, b) => (a.opId < b.opId ? -1 : a.opId > b.opId ? 1 : 0))
}

/**
 * ★ ต้องแยกนับ 'pending' กับ 'failed' เสมอ — ตัวที่ล้มเหลวถาวรแล้ว (ข้อมูลไม่ผ่านตรวจ)
 * จะไม่มีวันถูก readyOps() เลือกมาส่งซ้ำเองอีก ถ้านับรวมเป็น "รอส่ง" ตัวเลขจะค้าง
 * ไม่ลดเป็นศูนย์ตลอดกาลทั้งที่คิวเลิกพยายามส่งมันไปแล้ว — ผู้ใช้เห็นแล้วเข้าใจผิดว่า "ส่งไม่ไป"
 */
export function countByStatus(ops: readonly OutboxOp[]): { pending: number; failed: number } {
  let pending = 0
  let failed = 0
  for (const op of ops) {
    if (op.status === 'failed') failed += 1
    else pending += 1
  }
  return { pending, failed }
}

export function applyRetry(op: OutboxOp, message: string | undefined, now: number = Date.now()): OutboxOp {
  const attempt = op.attempt + 1
  return {
    ...op,
    attempt,
    nextAttemptAt: now + backoffMs(attempt),
    status: 'pending',
    ...(message === undefined ? {} : { error: message }),
  }
}

export function applyFailure(op: OutboxOp, message: string): OutboxOp {
  return { ...op, status: 'failed', error: message, attempt: op.attempt + 1 }
}
